import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { cx } from './cx';
import s from './EmptyState.module.css';

// The nine line illustrations from handoff/03-design/kits/empty-kit.html (120 × 96, navy strokes,
// cyan and gold accents, mist fills). Colours come from tokens through `style`, so no hex here.
export type EmptyArtName =
  | 'award'
  | 'letter'
  | 'chart'
  | 'inbox-done'
  | 'people'
  | 'calendar'
  | 'pool'
  | 'search'
  | 'shield';

export const EMPTY_ART_NAMES: readonly EmptyArtName[] = [
  'award',
  'letter',
  'chart',
  'inbox-done',
  'people',
  'calendar',
  'pool',
  'search',
  'shield',
];

const C = {
  navy: 'var(--brand-navy)',
  cyan: 'var(--brand-cyan)',
  gold: 'var(--brand-gold)',
  goldInk: 'var(--text-gold)',
  white: 'var(--white)',
  ground: 'var(--gray-50)',
  shadow: 'var(--gray-100)',
  line: 'var(--gray-200)',
  mist: 'var(--gray-300)',
  info: 'var(--info-bg)',
  warn: 'var(--warning-bg)',
  done: 'var(--success-bg)',
  doneInk: 'var(--status-done-ink)',
} as const;

const fill = (color: string): CSSProperties => ({ fill: color });
const line = (stroke: string, fillColor = 'none'): CSSProperties => ({ stroke, fill: fillColor });

const ART: Record<EmptyArtName, (floatClass: string | undefined) => ReactNode> = {
  award: (fl) => (
    <>
      <ellipse cx="60" cy="88" rx="34" ry="4" style={fill(C.shadow)} />
      <g className={fl}>
        <path
          d="M48 52 40 80l12-6 6 10 6-22"
          strokeWidth="2"
          strokeLinejoin="round"
          style={line(C.navy, C.info)}
        />
        <path
          d="M72 52l8 28-12-6-6 10-6-22"
          strokeWidth="2"
          strokeLinejoin="round"
          style={line(C.navy, C.info)}
        />
        <circle cx="60" cy="38" r="22" strokeWidth="2" style={line(C.navy, C.white)} />
        <circle cx="60" cy="38" r="14" strokeWidth="2" style={line(C.gold, C.warn)} />
        <path
          d="m60 30 2.6 5.4 5.9.8-4.3 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8-4.3-4.1 5.9-.8z"
          style={fill(C.gold)}
        />
      </g>
    </>
  ),
  letter: (fl) => (
    <>
      <ellipse cx="60" cy="88" rx="34" ry="4" style={fill(C.shadow)} />
      <g className={fl}>
        <rect
          x="30"
          y="30"
          width="60"
          height="44"
          rx="3"
          strokeWidth="2"
          style={line(C.navy, C.white)}
        />
        <path d="m30 33 30 22 30-22" strokeWidth="2" strokeLinejoin="round" style={line(C.navy)} />
        <rect
          x="42"
          y="12"
          width="36"
          height="30"
          rx="2"
          strokeWidth="2"
          style={line(C.cyan, C.ground)}
        />
        <path d="M48 21h24M48 28h16" strokeWidth="2" strokeLinecap="round" style={line(C.cyan)} />
      </g>
    </>
  ),
  chart: (fl) => (
    <>
      <path d="M20 76h82" strokeWidth="2" strokeLinecap="round" style={line(C.navy)} />
      <g className={fl}>
        <rect
          x="30"
          y="56"
          width="12"
          height="20"
          rx="2"
          strokeDasharray="3 3"
          style={line(C.mist, C.shadow)}
        />
        <rect
          x="50"
          y="44"
          width="12"
          height="32"
          rx="2"
          strokeDasharray="3 3"
          style={line(C.mist, C.shadow)}
        />
        <rect
          x="70"
          y="32"
          width="12"
          height="44"
          rx="2"
          strokeWidth="2"
          style={line(C.cyan, C.info)}
        />
        <path d="M76 20v6M73 23h6" strokeWidth="2" strokeLinecap="round" style={line(C.gold)} />
      </g>
    </>
  ),
  'inbox-done': (fl) => (
    <>
      <ellipse cx="60" cy="88" rx="34" ry="4" style={fill(C.shadow)} />
      <path
        d="M26 54l10-26h48l10 26v20a4 4 0 0 1-4 4H30a4 4 0 0 1-4-4z"
        strokeWidth="2"
        strokeLinejoin="round"
        style={line(C.navy, C.white)}
      />
      <path
        d="M26 54h20l4 8h20l4-8h20"
        strokeWidth="2"
        strokeLinejoin="round"
        style={line(C.navy)}
      />
      <g className={fl}>
        <circle cx="60" cy="30" r="14" strokeWidth="2" style={line(C.doneInk, C.done)} />
        <path
          d="m53 30 5 5 9-9"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={line(C.doneInk)}
        />
      </g>
    </>
  ),
  people: (fl) => (
    <>
      <ellipse cx="60" cy="88" rx="38" ry="4" style={fill(C.shadow)} />
      <circle cx="38" cy="42" r="10" strokeWidth="2" style={line(C.mist, C.white)} />
      <path d="M22 76c2-12 9-18 16-18s14 6 16 18" strokeWidth="2" style={line(C.mist, C.ground)} />
      <circle cx="82" cy="42" r="10" strokeWidth="2" style={line(C.mist, C.white)} />
      <path d="M66 76c2-12 9-18 16-18s14 6 16 18" strokeWidth="2" style={line(C.mist, C.ground)} />
      <g className={fl}>
        <circle cx="60" cy="34" r="12" strokeWidth="2" style={line(C.navy, C.white)} />
        <path d="M40 78c2-14 11-21 20-21s18 7 20 21" strokeWidth="2" style={line(C.navy, C.info)} />
        <circle cx="72" cy="22" r="5" style={fill(C.gold)} />
      </g>
    </>
  ),
  calendar: (fl) => (
    <>
      <ellipse cx="60" cy="88" rx="34" ry="4" style={fill(C.shadow)} />
      <g className={fl}>
        <rect
          x="30"
          y="20"
          width="60"
          height="58"
          rx="4"
          strokeWidth="2"
          style={line(C.navy, C.white)}
        />
        <path d="M30 34h60" strokeWidth="2" style={line(C.navy)} />
        <path d="M44 14v12M76 14v12" strokeWidth="2" strokeLinecap="round" style={line(C.navy)} />
        <g style={fill(C.shadow)}>
          <rect x="38" y="42" width="8" height="8" rx="1" />
          <rect x="52" y="42" width="8" height="8" rx="1" />
          <rect x="66" y="42" width="8" height="8" rx="1" />
          <rect x="38" y="56" width="8" height="8" rx="1" />
          <rect x="52" y="56" width="8" height="8" rx="1" />
        </g>
        <rect x="66" y="56" width="8" height="8" rx="1" style={fill(C.gold)} />
      </g>
    </>
  ),
  pool: (fl) => (
    <>
      <ellipse cx="60" cy="88" rx="34" ry="4" style={fill(C.shadow)} />
      <g className={fl}>
        <ellipse cx="60" cy="70" rx="26" ry="7" strokeWidth="2" style={line(C.navy, C.info)} />
        <path d="M34 70V58M86 70V58" strokeWidth="2" style={line(C.navy)} />
        <ellipse cx="60" cy="58" rx="26" ry="7" strokeWidth="2" style={line(C.navy, C.white)} />
        <circle cx="60" cy="32" r="14" strokeWidth="2" style={line(C.gold, C.warn)} />
        <path
          d="M60 25v14M56 28.5h6a2.5 2.5 0 0 1 0 5h-4a2.5 2.5 0 0 0 0 5h6"
          strokeWidth="1.8"
          strokeLinecap="round"
          style={line(C.goldInk)}
        />
      </g>
    </>
  ),
  search: (fl) => (
    <>
      <ellipse cx="60" cy="88" rx="34" ry="4" style={fill(C.shadow)} />
      <rect
        x="24"
        y="22"
        width="52"
        height="56"
        rx="3"
        strokeWidth="2"
        style={line(C.mist, C.white)}
      />
      <path
        d="M34 36h32M34 46h24M34 56h28"
        strokeWidth="3"
        strokeLinecap="round"
        style={line(C.line)}
      />
      <g className={fl}>
        <circle cx="78" cy="48" r="14" strokeWidth="2.4" style={line(C.navy, C.white)} />
        <path d="m88 58 10 10" strokeWidth="3" strokeLinecap="round" style={line(C.navy)} />
        <path d="M73 48h10" strokeWidth="2" strokeLinecap="round" style={line(C.cyan)} />
      </g>
    </>
  ),
  shield: (fl) => (
    <>
      <ellipse cx="60" cy="88" rx="30" ry="4" style={fill(C.shadow)} />
      <g className={fl}>
        <path
          d="M60 14 84 24v20c0 16-10 28-24 34-14-6-24-18-24-34V24z"
          strokeWidth="2"
          strokeLinejoin="round"
          style={line(C.navy, C.white)}
        />
        <path
          d="m50 46 7 7 13-13"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={line(C.cyan)}
        />
      </g>
    </>
  ),
};

export interface EmptyArtProps {
  name: EmptyArtName;
  size?: 'md' | 'sm';
  className?: string;
}

/** One of the nine empty-state line illustrations. Decorative: the headline carries the meaning. */
export function EmptyArt({ name, size = 'md', className }: EmptyArtProps): ReactElement {
  return (
    <svg
      className={cx(s.art, size === 'sm' && s.artSm, className)}
      viewBox="0 0 120 96"
      fill="none"
      aria-hidden="true"
      focusable="false"
      data-art={name}
    >
      {ART[name](s.float)}
    </svg>
  );
}
